import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box, Typography, CircularProgress, Chip, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, FormControl, InputLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";

const API_URL = process.env.REACT_APP_API_URL;

const dark = {
  bg: "#1a1a2e", card: "#16213e", border: "#394867",
  text: "#e0e0e0", textSecondary: "#9e9e9e", textMuted: "#616161", input: "#0f3460", inputBorder: "#394867",
};

const darkTextField = {
  "& .MuiOutlinedInput-root": { color: dark.text, backgroundColor: dark.input, "& fieldset": { borderColor: dark.inputBorder }, "&:hover fieldset": { borderColor: "#5a6f8a" }, "&.Mui-focused fieldset": { borderColor: "#64b5f6" } },
  "& .MuiInputLabel-root": { color: dark.textSecondary }, "& .MuiInputLabel-root.Mui-focused": { color: "#64b5f6" },
};

const darkDialog = { "& .MuiDialog-paper": { backgroundColor: dark.card, color: dark.text, border: `1px solid ${dark.border}` } };
const darkSelect = { color: dark.text, backgroundColor: dark.input, "& .MuiOutlinedInput-notchedOutline": { borderColor: dark.inputBorder }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#5a6f8a" }, "& .MuiSvgIcon-root": { color: dark.textSecondary } };

const MediaSection = () => {
  const [media, setMedia] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalWithAlt, setTotalWithAlt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, with-alt, missing-alt
  const [platformFilter, setPlatformFilter] = useState("all");
  const [editImage, setEditImage] = useState(null);
  const [editAltText, setEditAltText] = useState("");
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => { fetchMedia(); }, []);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/media`);
      setMedia(response.data.data?.media || []);
      setTotal(response.data.data?.total || 0);
      setTotalWithAlt(response.data.data?.totalWithAlt || 0);
    } catch (err) { console.error("Failed to fetch media:", err); }
    finally { setLoading(false); }
  };

  const handleSaveAlt = async () => {
    if (!editImage) return;
    try {
      await axios.patch(`${API_URL}/api/admin/media/${editImage.media_id}`, { alt_text: editAltText });
      setMedia(media.map((m) => m.media_id === editImage.media_id ? { ...m, alt_text: editAltText } : m));
      const updated = media.map((m) => m.media_id === editImage.media_id ? { ...m, alt_text: editAltText } : m);
      setTotalWithAlt(updated.filter((m) => m.alt_text && m.alt_text.trim()).length);
      setEditImage(null);
    } catch (err) { console.error("Failed to save:", err); }
  };

  const filteredMedia = media.filter((m) => {
    if (filter === "with-alt" && (!m.alt_text || !m.alt_text.trim())) return false;
    if (filter === "missing-alt" && m.alt_text && m.alt_text.trim()) return false;
    if (platformFilter !== "all" && m.platform !== platformFilter) return false;
    return true;
  });

  const platforms = ["all", ...new Set(media.map((m) => m.platform).filter(Boolean))];

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: "#fff" }} /></Box>;

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight="bold" sx={{ color: "#fff" }}>Media Library</Typography>
        <Box display="flex" gap={2} mt={0.5}>
          <Typography variant="caption" sx={{ color: dark.textSecondary }}>{total} images</Typography>
          <Typography variant="caption" sx={{ color: totalWithAlt === total && total > 0 ? "#66bb6a" : "#ffb74d" }}>{totalWithAlt}/{total} with alt text</Typography>
          <Typography variant="caption" sx={{ color: "#ef5350" }}>{total - totalWithAlt} missing alt</Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        {[
          { label: "All", value: "all" },
          { label: "With Alt", value: "with-alt" },
          { label: "Missing Alt", value: "missing-alt" },
        ].map((f) => (
          <Chip key={f.value} label={f.label} size="small" onClick={() => setFilter(f.value)}
            sx={{ cursor: "pointer", backgroundColor: filter === f.value ? "#1565c0" : dark.card, color: filter === f.value ? "#fff" : dark.textSecondary, border: `1px solid ${filter === f.value ? "#1565c0" : dark.border}` }} />
        ))}
        {platforms.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ color: dark.textSecondary }}>Platform</InputLabel>
            <Select value={platformFilter} label="Platform" onChange={(e) => setPlatformFilter(e.target.value)} sx={darkSelect}>
              <MenuItem value="all">All Platforms</MenuItem>
              {platforms.filter((p) => p !== "all").map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Grid */}
      {filteredMedia.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center", backgroundColor: dark.card, border: `1px solid ${dark.border}`, borderRadius: 2 }}>
          <Typography sx={{ color: dark.textSecondary }}>No media found.</Typography>
        </Box>
      ) : (
        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={2}>
          {filteredMedia.map((item) => {
            const hasAlt = item.alt_text && item.alt_text.trim();
            return (
              <Box key={item.media_id} sx={{ border: `1px solid ${dark.border}`, borderRadius: 2, overflow: "hidden", backgroundColor: dark.bg }}>
                {/* Image */}
                <Box sx={{ aspectRatio: "16/10", overflow: "hidden", cursor: "pointer" }} onClick={() => setLightboxImage(item)}>
                  <img src={item.url} alt={item.alt_text || ""} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </Box>

                {/* Info */}
                <Box sx={{ p: 1.5 }}>
                  {/* Alt status */}
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    {hasAlt ? (
                      <CheckCircleIcon sx={{ fontSize: 14, color: "#66bb6a" }} />
                    ) : (
                      <WarningIcon sx={{ fontSize: 14, color: "#ffb74d" }} />
                    )}
                    <Typography sx={{ fontSize: "0.7rem", color: hasAlt ? "#66bb6a" : "#ffb74d" }}>
                      {hasAlt ? "Alt text set" : "Missing alt text"}
                    </Typography>
                  </Box>

                  {/* Alt text preview */}
                  {hasAlt && (
                    <Typography sx={{ fontSize: "0.65rem", color: dark.textSecondary, mb: 0.5, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {item.alt_text}
                    </Typography>
                  )}

                  {/* Post association */}
                  {item.post_title && (
                    <Typography sx={{ fontSize: "0.6rem", color: dark.textMuted, mb: 0.5 }} title={item.post_title}>
                      Post: {item.post_title.substring(0, 30)}{item.post_title.length > 30 ? "..." : ""}
                    </Typography>
                  )}

                  {/* Meta row */}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography sx={{ fontSize: "0.6rem", color: dark.textMuted }}>{item.width}x{item.height} &middot; {item.file_size ? `${(item.file_size / 1024).toFixed(0)}KB` : ""}</Typography>
                    </Box>
                    <Box display="flex" gap={0.3}>
                      <IconButton size="small" onClick={() => { setEditImage(item); setEditAltText(item.alt_text || ""); }} sx={{ color: dark.textMuted, "&:hover": { color: "#ffb74d" }, p: 0.3 }} title="Edit alt text">
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => {
                        navigator.clipboard.writeText(`![${item.alt_text || "image"}](${item.url})`);
                        const btn = e.currentTarget;
                        btn.style.color = "#66bb6a";
                        setTimeout(() => { btn.style.color = ""; }, 1000);
                      }} sx={{ color: dark.textMuted, "&:hover": { color: "#64b5f6" }, p: 0.3 }} title="Copy Markdown">
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Edit Alt Text Dialog */}
      <Dialog open={!!editImage} onClose={() => setEditImage(null)} maxWidth="sm" fullWidth sx={darkDialog}>
        <DialogTitle sx={{ borderBottom: `1px solid ${dark.border}` }}>Edit Alt Text</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {editImage && (
            <Box>
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <img src={editImage.url} alt="" style={{ maxHeight: 200, maxWidth: "100%", objectFit: "contain", borderRadius: 4 }} />
                <Typography variant="caption" sx={{ display: "block", color: dark.textMuted, mt: 0.5 }}>{editImage.width}x{editImage.height} &middot; {editImage.file_size ? `${(editImage.file_size / 1024).toFixed(0)}KB` : ""}</Typography>
              </Box>
              <TextField label="Alt Text" value={editAltText} onChange={(e) => setEditAltText(e.target.value)} fullWidth size="small" multiline rows={3} placeholder="Describe this image for accessibility and SEO" sx={darkTextField} />
              {editImage.post_title && (
                <Typography variant="caption" sx={{ color: dark.textMuted, mt: 1, display: "block" }}>Associated with: {editImage.post_title}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${dark.border}`, p: 2 }}>
          <Button onClick={() => setEditImage(null)} sx={{ color: dark.textSecondary }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAlt} sx={{ backgroundColor: "#1565c0", "&:hover": { backgroundColor: "#0d47a1" } }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox */}
      {lightboxImage && (
        <Box onClick={() => setLightboxImage(null)} sx={{ position: "fixed", inset: 0, zIndex: 9999, backgroundColor: "rgba(0,0,0,0.92)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <img src={lightboxImage.url} alt="" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain", cursor: "default", borderRadius: 4 }}
            onLoad={(e) => { const d = document.getElementById("media-lb-dims"); if (d) d.textContent = `${e.target.naturalWidth} x ${e.target.naturalHeight}`; }} />
          <Box sx={{ mt: 2, display: "flex", gap: 3, alignItems: "center" }}>
            <Typography id="media-lb-dims" sx={{ color: "#fff", fontSize: "0.85rem", fontFamily: "monospace" }}>Loading...</Typography>
            <Typography sx={{ color: dark.textSecondary, fontSize: "0.8rem" }}>{lightboxImage.file_size ? `${(lightboxImage.file_size / 1024).toFixed(0)} KB` : ""}</Typography>
            {lightboxImage.alt_text && <Typography sx={{ color: "#64b5f6", fontSize: "0.8rem", maxWidth: 400, textAlign: "center" }}>"{lightboxImage.alt_text}"</Typography>}
          </Box>
          <Typography sx={{ color: dark.textMuted, fontSize: "0.7rem", mt: 1 }}>Click anywhere to close</Typography>
        </Box>
      )}
    </Box>
  );
};

export default MediaSection;
